export const widgetStyles = `
:host { all: initial; font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; }

* { -webkit-tap-highlight-color: transparent; }

/* Kill the double-tap-to-zoom gesture inside the widget. Pinch zoom and
   scrolling still work. */
.cc-root, .cc-root * { touch-action: manipulation; }
.cc-messages, .cc-escalate-form { touch-action: pan-y; }

.cc-root {
  position: fixed;
  bottom: calc(20px + env(safe-area-inset-bottom));
  right: calc(20px + env(safe-area-inset-right));
  /* Intercom's value. Max int32 leaves a host no room to deliberately go above. */
  z-index: var(--cc-z, 2147483000);
  color: #111;
}

@keyframes cc-bubble-pulse {
  0%, 100% { box-shadow: 0 10px 30px rgba(0,0,0,0.15), 0 0 0 0 color-mix(in srgb, var(--cc-accent, #4f46e5) 45%, transparent); }
  50% { box-shadow: 0 10px 30px rgba(0,0,0,0.15), 0 0 0 12px color-mix(in srgb, var(--cc-accent, #4f46e5) 0%, transparent); }
}
@keyframes cc-bubble-in {
  from { transform: scale(0.5); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
@keyframes cc-panel-in {
  from { transform: translateY(12px) scale(0.96); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}
@keyframes cc-panel-out {
  from { transform: translateY(0) scale(1); opacity: 1; }
  to { transform: translateY(12px) scale(0.96); opacity: 0; }
}
@keyframes cc-dot {
  0%, 80%, 100% { transform: scale(0.65); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}

.cc-bubble {
  height: 56px; min-width: 56px; border-radius: 9999px; border: none;
  background: var(--cc-accent, #4f46e5); color: var(--cc-accent-ink, white); cursor: pointer;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  animation: cc-bubble-in 0.28s cubic-bezier(0.18, 0.89, 0.32, 1.28);
  padding: 0 16px 0 20px;
}
.cc-bubble:not(.cc-bubble-open) svg { width: 22px; height: 22px; }
.cc-bubble.cc-bubble-open { width: 56px; padding: 0; }
.cc-bubble-label {
  font-size: 13px; font-weight: 600; letter-spacing: 0.01em;
  white-space: nowrap; line-height: 1;
  animation: cc-bubble-icon-in 0.18s ease-out;
}
.cc-bubble:hover { transform: translateY(-2px) scale(1.05); box-shadow: 0 14px 36px rgba(0,0,0,0.22); }
.cc-bubble:active { transform: translateY(0) scale(0.97); }
.cc-bubble.cc-bubble-pulse { animation: cc-bubble-in 0.28s cubic-bezier(0.18, 0.89, 0.32, 1.28), cc-bubble-pulse 2.4s ease-in-out 0.4s 3; }
.cc-bubble svg { width: 26px; height: 26px; animation: cc-bubble-icon-in 0.18s ease-out; }
.cc-bubble.cc-bubble-open { box-shadow: 0 10px 30px rgba(0,0,0,0.22); }
@keyframes cc-bubble-icon-in {
  from { transform: scale(0.5) rotate(-45deg); opacity: 0; }
  to { transform: scale(1) rotate(0); opacity: 1; }
}

.cc-panel {
  position: absolute; bottom: 72px; right: 0;
  width: 380px; max-width: calc(100vw - 40px);
  height: 560px; max-height: calc(100vh - 100px);
  background: white; border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.08);
  display: flex; flex-direction: column; overflow: hidden;
  border: 1px solid rgba(0,0,0,0.06);
  transform-origin: bottom right;
  animation: cc-panel-in 0.22s cubic-bezier(0.18, 0.89, 0.32, 1.12);
  /* Set by JS (visualViewport listener): --cc-kb is the on-screen keyboard
     height (0 when closed), --cc-gap the room kept under the panel for the
     bubble. Mobile-only in effect; the desktop rules above ignore both. */
  --cc-kb: 0px;
  --cc-gap: 72px;
  transition: transform 0.22s cubic-bezier(0.22, 0.61, 0.36, 1),
              height 0.22s cubic-bezier(0.22, 0.61, 0.36, 1),
              bottom 0.22s cubic-bezier(0.22, 0.61, 0.36, 1);
}
.cc-panel.cc-panel-closing { animation: cc-panel-out 0.16s ease-in forwards; }

@media (max-width: 640px) {
  .cc-root {
    bottom: calc(16px + env(safe-area-inset-bottom));
    right: calc(16px + env(safe-area-inset-right));
  }
  .cc-panel {
    /* Stay docked to the right like desktop, just sized for phone screens.
       JS sets two vars from visualViewport: --cc-kb is the keyboard height,
       --cc-gap is the room reserved under the panel for the bubble. When the
       keyboard opens the bubble is behind it, so the gap collapses and the
       panel keeps as much height as the visible area allows. The max(220px, ...)
       floor stops the panel from collapsing to zero if the reading is wrong. */
    position: absolute;
    right: 0;
    bottom: var(--cc-gap, 72px);
    width: min(380px, calc(100vw - 24px));
    max-width: calc(100vw - 24px);
    height: min(640px, max(220px, calc(100dvh - var(--cc-gap, 72px) - var(--cc-kb, 0px) - 40px)));
    max-height: none;
    border-radius: 18px;
    transform: translateY(calc(-1 * var(--cc-kb, 0px)));
  }
  .cc-header .cc-close {
    width: 40px; height: 40px; padding: 0;
    display: grid; place-items: center;
  }
  .cc-header .cc-close svg { width: 18px; height: 18px; }
  .cc-messages {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
  /* iOS Safari zooms the whole page when it focuses a field under 16px.
     The base .cc-input / .cc-escalate-form rules sit later in this sheet, so
     these selectors carry an extra class to win the cascade by specificity
     instead of by source order. */
  .cc-panel .cc-input,
  .cc-panel .cc-escalate-form input,
  .cc-panel .cc-escalate-form textarea {
    font-size: 16px;
  }
  .cc-send { width: 44px; height: 44px; }
  .cc-bubble:hover { transform: none; box-shadow: 0 10px 30px rgba(0,0,0,0.15); }
}

@media (prefers-color-scheme: dark) {
  .cc-panel { background: #1f2937; color: #f9fafb; border-color: #374151; }
  .cc-input-bar { background: #1f2937; border-color: #374151; }
  .cc-input { background: #374151; color: #f9fafb; border-color: #4b5563; }
  .cc-input::placeholder { color: #9ca3af; }
  .cc-message.assistant { background: #374151; color: #f9fafb; }
  .cc-escalate-form input, .cc-escalate-form textarea { background: #374151; color: #f9fafb; border-color: #4b5563; }
  .cc-escalate-form input::placeholder, .cc-escalate-form textarea::placeholder { color: #9ca3af; }
  .cc-escalate-form .cc-cancel { background: #374151; color: #f9fafb; }
  .cc-footer-brand { border-color: #374151; color: #6b7280; }
  .cc-typing-dot { background: #6b7280; }
  .cc-reset { color: #9ca3af; }
  .cc-reset:hover { color: #e5e7eb; }
}

.cc-header {
  padding: 14px 16px; border-bottom: 1px solid rgba(0,0,0,0.06);
  background: var(--cc-accent, #4f46e5); color: var(--cc-accent-ink, white);
  display: flex; justify-content: space-between; align-items: center;
  gap: 8px; flex-shrink: 0;
}
.cc-header .cc-title-wrap { display: flex; align-items: center; gap: 10px; min-width: 0; }
.cc-header .cc-avatar {
  width: 32px; height: 32px; border-radius: 9999px;
  background: rgba(255,255,255,0.22);
  display: grid; place-items: center; flex-shrink: 0;
}
.cc-header .cc-avatar svg { width: 18px; height: 18px; }
.cc-header .cc-title-col { display: flex; flex-direction: column; min-width: 0; }
.cc-header .cc-title { font-weight: 600; font-size: 14px; line-height: 1.2; }
.cc-header .cc-subtitle { font-size: 11px; opacity: 0.85; display: flex; align-items: center; gap: 4px; }
.cc-header .cc-status-dot {
  width: 6px; height: 6px; border-radius: 9999px; background: #4ade80;
  box-shadow: 0 0 0 2px rgba(74, 222, 128, 0.25);
}
.cc-header-actions { display: flex; align-items: center; gap: 4px; }
.cc-header button {
  background: transparent; border: none; color: var(--cc-accent-ink, white); cursor: pointer;
  padding: 6px 10px; font-size: 12px; border-radius: 8px;
  display: inline-flex; align-items: center; gap: 4px;
  transition: background 0.15s ease;
}
.cc-header .cc-escalate { background: rgba(255,255,255,0.18); font-weight: 500; }
.cc-header .cc-escalate:hover { background: rgba(255,255,255,0.3); }
.cc-header .cc-close { padding: 6px; }
.cc-header .cc-close:hover { background: rgba(255,255,255,0.2); }
.cc-header .cc-close svg { width: 16px; height: 16px; }

.cc-messages {
  flex: 1; overflow-y: auto; padding: 16px;
  display: flex; flex-direction: column; gap: 10px;
  scroll-behavior: smooth;
}
.cc-messages::-webkit-scrollbar { width: 6px; }
.cc-messages::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 9999px; }

.cc-message {
  max-width: 85%; padding: 10px 14px; border-radius: 16px;
  font-size: 14px; line-height: 1.45; white-space: pre-wrap; word-wrap: break-word;
  animation: cc-panel-in 0.18s ease-out;
}
.cc-message.user { align-self: flex-end; background: var(--cc-accent, #4f46e5); color: var(--cc-accent-ink, white); border-bottom-right-radius: 4px; }
.cc-message.assistant { align-self: flex-start; background: #f3f4f6; color: #111; border-bottom-left-radius: 4px; }
.cc-message.assistant a { color: var(--cc-accent, #4f46e5); text-decoration: underline; word-break: break-word; }
.cc-message.assistant strong { font-weight: 600; }
.cc-message.assistant em { font-style: italic; }
.cc-message.system {
  align-self: center; font-size: 12px; color: #6b7280; font-style: italic;
  max-width: 100%; background: transparent; padding: 2px 8px; text-align: center;
}

.cc-typing {
  align-self: flex-start;
  background: #f3f4f6; padding: 12px 14px; border-radius: 16px; border-bottom-left-radius: 4px;
  display: inline-flex; gap: 4px; align-items: center;
}
@media (prefers-color-scheme: dark) { .cc-typing { background: #374151; } }
.cc-typing-dot {
  width: 6px; height: 6px; border-radius: 9999px; background: #9ca3af;
  animation: cc-dot 1.2s ease-in-out infinite;
}
.cc-typing-dot:nth-child(2) { animation-delay: 0.16s; }
.cc-typing-dot:nth-child(3) { animation-delay: 0.32s; }

.cc-input-bar {
  border-top: 1px solid rgba(0,0,0,0.06); padding: 10px;
  display: flex; gap: 8px; align-items: flex-end; flex-shrink: 0;
}
.cc-input {
  flex: 1; border: 1px solid #d1d5db; border-radius: 12px; padding: 10px 12px;
  font: inherit; font-size: 14px; resize: none; outline: none;
  min-height: 40px; max-height: 120px;
  background: white; color: #111;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.cc-input:focus {
  border-color: var(--cc-accent, #4f46e5);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--cc-accent, #4f46e5) 20%, transparent);
}
.cc-send {
  border: none; background: var(--cc-accent, #4f46e5); color: var(--cc-accent-ink, white);
  width: 40px; height: 40px; border-radius: 12px; cursor: pointer;
  display: grid; place-items: center; flex-shrink: 0;
  transition: transform 0.12s ease, opacity 0.15s ease;
}
.cc-send:hover:not(:disabled) { transform: translateY(-1px); }
.cc-send:active:not(:disabled) { transform: translateY(0); }
.cc-send:disabled { opacity: 0.4; cursor: not-allowed; }
.cc-send svg { width: 18px; height: 18px; }

.cc-escalate-form { padding: 16px; display: flex; flex-direction: column; gap: 10px; flex: 1; overflow-y: auto; }
.cc-escalate-form h3 { margin: 0; font-size: 15px; font-weight: 600; }
.cc-escalate-form p { margin: 0 0 4px; font-size: 13px; color: #6b7280; }
.cc-escalate-form label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: inherit; opacity: 0.78; font-weight: 500; }
.cc-escalate-form label:focus-within { opacity: 1; }
.cc-escalate-form input, .cc-escalate-form textarea {
  border: 1px solid #d1d5db; border-radius: 8px; padding: 8px 12px;
  font: inherit; font-size: 14px; outline: none;
  background: white; color: #111;
  transition: border-color 0.15s ease;
}
.cc-escalate-form input:focus, .cc-escalate-form textarea:focus { border-color: var(--cc-accent, #4f46e5); }
.cc-escalate-form .cc-actions { display: flex; justify-content: space-between; margin-top: 8px; gap: 6px; }
.cc-escalate-form button {
  border: none; border-radius: 10px; padding: 10px 16px; cursor: pointer;
  font-size: 14px; font-weight: 500;
  transition: transform 0.12s ease, opacity 0.15s ease;
}
.cc-escalate-form button:active { transform: translateY(1px); }
.cc-escalate-form .cc-submit { background: var(--cc-accent, #4f46e5); color: var(--cc-accent-ink, white); flex: 1; }
.cc-escalate-form .cc-submit:disabled { opacity: 0.5; cursor: not-allowed; }
.cc-escalate-form .cc-cancel { background: #f3f4f6; color: #111; }
.cc-escalate-form .cc-whatsapp {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  background: #25d366; color: white; text-decoration: none;
  border: none; border-radius: 10px; padding: 10px 14px;
  font: inherit; font-size: 14px; font-weight: 600; cursor: pointer;
  transition: transform 0.12s ease, filter 0.15s ease;
  margin-top: 4px;
}
.cc-escalate-form .cc-whatsapp:hover { filter: brightness(0.96); }
.cc-escalate-form .cc-whatsapp:active { transform: translateY(1px); }
.cc-escalate-form .cc-whatsapp:disabled { opacity: 0.7; cursor: default; }
.cc-escalate-form .cc-whatsapp-blocked { color: #b45309; margin-top: -2px; }

.cc-success {
  padding: 32px 20px; text-align: center; flex: 1;
  display: flex; flex-direction: column; justify-content: center; align-items: center;
  gap: 4px;
}
.cc-success .cc-icon {
  width: 56px; height: 56px; border-radius: 9999px;
  background: var(--cc-accent, #4f46e5); color: var(--cc-accent-ink, white);
  display: grid; place-items: center; margin-bottom: 12px;
  animation: cc-bubble-in 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28);
}
.cc-success .cc-icon svg { width: 28px; height: 28px; }
.cc-success h3 { margin: 0; font-size: 16px; font-weight: 600; }
.cc-success p { margin: 0; font-size: 14px; color: #6b7280; }

.cc-footer-brand {
  text-align: center; font-size: 11px; color: #9ca3af;
  padding: 6px 8px; border-top: 1px solid rgba(0,0,0,0.06);
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  flex-shrink: 0;
}
.cc-footer-brand a { color: inherit; text-decoration: none; }
.cc-footer-brand a:hover { text-decoration: underline; }
.cc-reset {
  background: none; border: none; color: #9ca3af; cursor: pointer;
  font-size: 11px; padding: 2px 4px; border-radius: 4px;
  transition: color 0.15s ease;
}
.cc-reset:hover { color: #374151; }

.cc-error {
  padding: 8px 12px; background: #fef2f2; color: #991b1b;
  border-radius: 8px; font-size: 13px; margin: 8px 16px;
}
@media (prefers-color-scheme: dark) {
  .cc-error { background: #7f1d1d; color: #fef2f2; }
}
`

/**
 * The element Nuxt UI renders as the backdrop that dims the page. Modal,
 * Slideover and Drawer render it; Popover, Select, Combobox and DropdownMenu
 * do not. Exported so the stylesheet gate and the Escape guard in Chatbot.vue
 * read the same signal and cannot drift apart.
 */
export const OVERLAY_SELECTOR = '[data-slot="overlay"]'

/**
 * Injected into the host page's head by main.ts. See the comment there for why
 * this cannot live in the shadow stylesheet and why it matches the backdrop's
 * existence rather than its data-state.
 */
export const overlayGateCss = `body:has(${OVERLAY_SELECTOR}) #cc-chatbot-host{visibility:hidden;pointer-events:none}`
